import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  SpinnerGap, MagnifyingGlass, Copy, Buildings, Briefcase,
  Heart, Palette, Megaphone, HandHeart, Cpu, ShoppingCart,
  ArrowLeft, Check, FunnelSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  Technology: Cpu,
  Commerce: ShoppingCart,
  Coaching: Heart,
  "Professional Services": Briefcase,
  Agency: Megaphone,
  Creative: Palette,
  Impact: HandHeart,
  General: Buildings,
};

const CATEGORY_COLORS = {
  Technology: "#3B82F6",
  Commerce: "#F59E0B",
  Coaching: "#EC4899",
  "Professional Services": "#8B5CF6",
  Agency: "#FF4400",
  Creative: "#06B6D4",
  Impact: "#10B981",
  General: "#6B7280",
};

export default function StrategyTemplatesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [cloning, setCloning] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      const params = selectedCategory ? `?category=${encodeURIComponent(selectedCategory)}` : "";
      const resp = await api.get(`/strategy/templates${params}`);
      setTemplates(resp.data?.templates || []);
      if (!selectedCategory) setCategories(resp.data?.categories || []);
    } catch {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [api, selectedCategory]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleClone = async (templateId) => {
    setCloning(templateId);
    try {
      const resp = await api.post(`/strategy/templates/${templateId}/clone`);
      const profile = resp.data?.strategy_profile;
      toast.success(`"${profile?.name}" added to your strategy profiles`);
      navigate("/settings");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clone template");
    } finally {
      setCloning(null);
    }
  };

  const filtered = templates.filter((t) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      t.name.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      t.category?.toLowerCase().includes(q) ||
      t.icp?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-4"
            data-testid="templates-back-btn"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-semibold text-[var(--text-primary)] mb-2" data-testid="templates-page-title">
            Strategy Templates
          </h1>
          <p className="text-[var(--text-secondary)] max-w-2xl">
            Start with a proven strategy profile tailored to your industry. Clone any template, then customize the ICP, positioning, and messaging to make it yours.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              className="w-full pl-10 pr-4 py-2.5 bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-lg text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="templates-search"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <FunnelSimple size={16} className="text-[var(--text-muted)] shrink-0" />
            <button
              onClick={() => setSelectedCategory("")}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                !selectedCategory
                  ? "bg-[#FF4400] text-white"
                  : "bg-[var(--bg-card)] text-[var(--text-secondary)] border border-[var(--border)] hover:border-[var(--border-strong)]"
              }`}
              data-testid="templates-filter-all"
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat === selectedCategory ? "" : cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat
                    ? "bg-[#FF4400] text-white"
                    : "bg-[var(--bg-card)] text-[var(--text-secondary)] border border-[var(--border)] hover:border-[var(--border-strong)]"
                }`}
                data-testid={`templates-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-16">
            <SpinnerGap size={28} className="animate-spin text-[#FF4400]" />
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-16">
            <Buildings size={40} className="mx-auto text-[var(--text-muted)] mb-3" />
            <p className="text-[var(--text-secondary)]">No templates found{searchQuery ? ` for "${searchQuery}"` : ""}.</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
            {filtered.map((t, idx) => {
              const CatIcon = CATEGORY_ICONS[t.category] || Buildings;
              const catColor = CATEGORY_COLORS[t.category] || "#6B7280";
              const isExpanded = expandedTemplate === t.template_id;

              return (
                <motion.div
                  key={t.template_id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03, duration: 0.25 }}
                  className={`group relative bg-[var(--bg-card)] border rounded-xl overflow-hidden transition-all duration-200 ${
                    isExpanded
                      ? "border-[#FF4400] ring-1 ring-[#FF4400]/20"
                      : "border-[var(--border)] hover:border-[var(--border-strong)] hover:shadow-md"
                  }`}
                  data-testid={`template-card-${t.template_id}`}
                >
                  <div className="p-4 pb-3">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${catColor}15` }}
                        >
                          <CatIcon size={16} weight="duotone" style={{ color: catColor }} />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-[var(--text-primary)] leading-tight">{t.name}</h3>
                          <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: catColor }}>{t.category}</span>
                        </div>
                      </div>
                      {t.usage_count > 0 && (
                        <span className="text-[10px] font-['JetBrains_Mono'] text-[var(--text-muted)] bg-[var(--bg-base)] px-1.5 py-0.5 rounded">
                          {t.usage_count} used
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] leading-relaxed line-clamp-2">{t.description}</p>
                  </div>

                  <div className="px-4 pb-3 space-y-2">
                    <div className="bg-[var(--bg-base)] rounded-lg p-2.5">
                      <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[var(--text-muted)]">ICP</span>
                      <p className="text-xs text-[var(--text-secondary)] mt-0.5 line-clamp-2">{t.icp || "Not defined"}</p>
                    </div>

                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-2"
                      >
                        <div className="bg-[var(--bg-base)] rounded-lg p-2.5">
                          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[var(--text-muted)]">Positioning</span>
                          <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t.positioning || "Not defined"}</p>
                        </div>
                        <div className="bg-[var(--bg-base)] rounded-lg p-2.5">
                          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[var(--text-muted)]">Offer</span>
                          <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t.offer || "Not defined"}</p>
                        </div>
                        {t.message_pillars?.length > 0 && (
                          <div className="bg-[var(--bg-base)] rounded-lg p-2.5">
                            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[var(--text-muted)]">Message Pillars</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {t.message_pillars.map((p, i) => (
                                <span key={`pillar-${p}-${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--accent-subtle)] text-[#FF4400]">{p}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {t.claims_to_avoid?.length > 0 && (
                          <div className="bg-[var(--bg-base)] rounded-lg p-2.5">
                            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[var(--text-muted)]">Claims to Avoid</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {t.claims_to_avoid.map((c, i) => (
                                <span key={`claim-${c}-${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">{c}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>

                  <div className="px-4 pb-4 flex items-center gap-2">
                    <button
                      onClick={() => setExpandedTemplate(isExpanded ? null : t.template_id)}
                      className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                      data-testid={`template-expand-${t.template_id}`}
                    >
                      {isExpanded ? "Show less" : "Show more"}
                    </button>
                    <div className="flex-1" />
                    <Button
                      size="sm"
                      disabled={cloning === t.template_id}
                      onClick={() => handleClone(t.template_id)}
                      className="bg-[#FF4400] hover:bg-[#FF6633] text-white text-xs h-8 px-4"
                      data-testid={`template-clone-${t.template_id}`}
                    >
                      {cloning === t.template_id ? (
                        <SpinnerGap size={14} className="animate-spin" />
                      ) : (
                        <><Copy size={14} className="mr-1" /> Use This Template</>
                      )}
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
